import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  ExternalLink,
  GitBranch,
  AlertTriangle,
  Shield,
  Globe2,
} from 'lucide-react';
import { getInstitutionSummary, getRiskScore } from '../api/client';
import type { RiskGrade } from '../types';

function ScoreBar({ label, score, weight }: { label: string; score: number | string; weight: string }) {
  const numScore = Number(score);
  const color =
    numScore <= 20 ? 'bg-green-500' :
    numScore <= 40 ? 'bg-lime-500' :
    numScore <= 60 ? 'bg-yellow-500' :
    numScore <= 80 ? 'bg-orange-500' :
    'bg-red-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-900 font-medium">{numScore.toFixed(0)}</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div
          className={`${color} h-2 rounded-full transition-all`}
          style={{ width: `${numScore}%` }}
        />
      </div>
      <p className="text-xs text-gray-400">{weight} weight</p>
    </div>
  );
}

function GradeBadge({ grade }: { grade: RiskGrade }) {
  const colors = {
    A: 'bg-green-100 text-green-800 border-green-200',
    B: 'bg-lime-100 text-lime-800 border-lime-200',
    C: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    D: 'bg-orange-100 text-orange-800 border-orange-200',
    F: 'bg-red-100 text-red-800 border-red-200',
  };

  const labels = {
    A: 'Low Risk',
    B: 'Low-Medium Risk',
    C: 'Medium Risk',
    D: 'High Risk',
    F: 'Very High Risk',
  };

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${colors[grade]}`}>
      <span className="text-3xl font-bold">{grade}</span>
      <span className="text-sm">{labels[grade]}</span>
    </div>
  );
}

export default function InstitutionDetail() {
  const { lei } = useParams<{ lei: string }>();

  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['institution', lei, 'summary'],
    queryFn: () => getInstitutionSummary(lei!),
    enabled: !!lei,
  });

  const { data: riskScore } = useQuery({
    queryKey: ['risk-score', lei],
    queryFn: () => getRiskScore(lei!),
    enabled: !!lei,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="card p-12 text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Institution Not Found</h2>
        <p className="text-gray-500">
          No institution found with LEI: {lei}
        </p>
        <Link to="/search" className="btn-primary mt-4 inline-block">
          Back to Search
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary-50 rounded-lg">
            <Building2 className="h-8 w-8 text-primary-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{summary.legal_name}</h1>
            {summary.trading_name && (
              <p className="text-gray-500">Trading as: {summary.trading_name}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="badge bg-gray-100 text-gray-700">
                {summary.primary_type.replace(/_/g, ' ')}
              </span>
              <span className="badge bg-gray-100 text-gray-700">
                <Globe2 className="h-3 w-3 mr-1" />
                {summary.jurisdiction_country}
              </span>
              {summary.is_gsib && (
                <span className="badge bg-purple-100 text-purple-800">G-SIB</span>
              )}
            </div>
            <p className="font-mono text-sm text-gray-400 mt-2">{summary.lei}</p>
          </div>
          <div className="text-right">
            {summary.overall_grade && <GradeBadge grade={summary.overall_grade} />}
          </div>
        </div>

        {/* Quick links */}
        <div className="flex gap-3 mt-6 pt-6 border-t border-gray-100">
          <Link
            to={`/blast-radius/${lei}`}
            className="btn-secondary flex items-center gap-2"
          >
            <Shield className="h-4 w-4" />
            Blast Radius
          </Link>
          <Link
            to={`/ownership/${lei}`}
            className="btn-secondary flex items-center gap-2"
          >
            <GitBranch className="h-4 w-4" />
            Ownership Chain
          </Link>
          {summary.website_url && (
            <a
              href={summary.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Website
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Score Breakdown */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Risk Score Breakdown
          </h2>
          {riskScore ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">Overall Score</span>
                <span className="text-2xl font-bold text-gray-900">
                  {Number(riskScore.overall_score).toFixed(0)}/100
                </span>
              </div>

              <ScoreBar
                label="Data Collection"
                score={riskScore.data_collection_score}
                weight="15%"
              />
              <ScoreBar
                label="Data Retention"
                score={riskScore.data_retention_score}
                weight="10%"
              />
              <ScoreBar
                label="Data Sharing"
                score={riskScore.data_sharing_score}
                weight="20%"
              />
              <ScoreBar
                label="International Flows"
                score={riskScore.international_flows_score}
                weight="15%"
              />
              <ScoreBar
                label="Individual Control"
                score={riskScore.individual_control_score}
                weight="15%"
              />
              <ScoreBar
                label="Transparency"
                score={riskScore.transparency_score}
                weight="10%"
              />
              <ScoreBar
                label="Corporate Complexity"
                score={riskScore.corporate_complexity_score}
                weight="10%"
              />
              <ScoreBar
                label="Track Record"
                score={riskScore.track_record_score}
                weight="5%"
              />

              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Confidence: {Number(riskScore.confidence_score).toFixed(0)}%
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Calculated: {new Date(riskScore.calculated_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No risk score available for this institution.
            </div>
          )}
        </div>

        {/* Corporate & Enforcement Info */}
        <div className="space-y-6">
          {/* Corporate Group */}
          {summary.group_name && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Corporate Group
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Group Name</span>
                  <span className="text-gray-900">{summary.group_name}</span>
                </div>
                {summary.ultimate_parent_lei && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ultimate Parent</span>
                    <Link
                      to={`/institution/${summary.ultimate_parent_lei}`}
                      className="text-primary-600 hover:text-primary-700 font-mono text-sm"
                    >
                      {summary.ultimate_parent_lei}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enforcement Summary */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Enforcement History
            </h2>
            {summary.enforcement_total_issues ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Issues</span>
                  <span className="text-gray-900 font-medium">
                    {summary.enforcement_total_issues}
                  </span>
                </div>
                {summary.enforcement_total_fines_eur !== undefined && summary.enforcement_total_fines_eur > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Fines</span>
                    <span className="text-red-600 font-medium">
                      €{summary.enforcement_total_fines_eur.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No enforcement issues on record.
              </p>
            )}
          </div>

          {/* Blast Radius Preview */}
          {summary.blast_radius_score !== undefined && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Blast Radius
              </h2>
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-500">Impact Score</span>
                <span className="text-2xl font-bold text-gray-900">
                  {Number(summary.blast_radius_score).toFixed(0)}
                </span>
              </div>
              <Link
                to={`/blast-radius/${lei}`}
                className="btn-primary w-full justify-center"
              >
                View Full Analysis
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
